import { PitchSlideView } from '@src/components/sco/PitchDeckAuditor/model/types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'

/**
 * Pitch Deck Auditor slide-access policy.
 *
 * Any logged-in user can open the auditor. Reading the full audit requires the
 * `canAccessPitchDeckAuditor` content permission — without it, only the first
 * {@link FREE_SLIDE_LIMIT} slides are readable and the rest are paywalled behind
 * the `scom-accelerator-tools` product.
 */
export const FREE_SLIDE_LIMIT = 2

/** Product a user must purchase to unlock the full deck audit. */
export const PITCH_DECK_UPGRADE_PRODUCT_ID = 'scom-accelerator-tools'

/** A slide is gated when it falls beyond the free preview (slides are 1-indexed by `position`). */
export function isSlideBeyondFreeLimit(slide: Pick<PitchSlideView, 'position'>): boolean {
  return slide.position > FREE_SLIDE_LIMIT
}

export function usePitchDeckSlideAccess() {
  const { currentUserHasContentPermission } = useCurrentUser()
  const canAccessFullDeck = currentUserHasContentPermission('canAccessPitchDeckAuditor')

  return {
    canAccessFullDeck,
    freeSlideLimit: FREE_SLIDE_LIMIT,
    /** True when the user must upgrade before reading this slide. */
    isSlideLocked: (slide: Pick<PitchSlideView, 'position'>) =>
      !canAccessFullDeck && isSlideBeyondFreeLimit(slide),
  }
}
